import React from 'react';
import type { Editor } from '@tiptap/react';
import { PENDING_FILE_PREFIX, escapeHtml, type PendingUpload } from '../richTextMediaHelpers';

type UseDeferredMediaUploadsParams = {
  editor: Editor | null;
  value: string;
  onChange: (content: string) => void;
  canUploadMedia: boolean;
  uploadContextId?: string | number;
  uploadMedia: (file: File, type: 'image' | 'file') => Promise<string>;
  normalizeContentForPersisting: (content: string) => string;
  scheduleResolvedMediaSync: (immediate?: boolean) => Promise<void>;
  maxImageWidth: number;
  maxImageHeight: number;
};

type UseDeferredMediaUploadsResult = {
  imageInputRef: React.RefObject<HTMLInputElement | null>;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  isFlushingPending: boolean;
  mediaError: string | null;
  hasPendingUploads: boolean;
  flushPendingUploads: () => Promise<void>;
  handleImageInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleFileInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleUpload: (file: File, type: 'image' | 'file') => void;
};

export const useDeferredMediaUploads = ({
  editor,
  value,
  onChange,
  canUploadMedia,
  uploadContextId,
  uploadMedia,
  normalizeContentForPersisting,
  scheduleResolvedMediaSync,
  maxImageWidth,
  maxImageHeight,
}: UseDeferredMediaUploadsParams): UseDeferredMediaUploadsResult => {
  const imageInputRef = React.useRef<HTMLInputElement | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const pendingUploadsRef = React.useRef<Map<string, PendingUpload>>(new Map());

  const [isFlushingPending, setIsFlushingPending] = React.useState(false);
  const [pendingUploadsCount, setPendingUploadsCount] = React.useState(0);
  const [mediaError, setMediaError] = React.useState<string | null>(null);

  const insertImage = React.useCallback(
    (url: string, fileName: string) => {
      if (!editor) return;

      editor
        .chain()
        .focus()
        .insertContent(
          `<img src="${escapeHtml(url)}" alt="${escapeHtml(fileName)}" style="max-width: ${maxImageWidth}px; max-height: ${maxImageHeight}px;" />`
        )
        .run();
    },
    [editor, maxImageHeight, maxImageWidth]
  );

  const insertFileLink = React.useCallback(
    (url: string, fileName: string) => {
      if (!editor) return;

      const safeUrl = escapeHtml(url);
      const safeFileName = escapeHtml(fileName);
      editor
        .chain()
        .focus()
        .insertContent(
          `<div><a href="${safeUrl}" target="_blank" rel="noopener noreferrer" style="cursor: pointer;">${safeFileName}</a></div>`
        )
        .run();
    },
    [editor]
  );

  const handleUpload = React.useCallback(
    (file: File, type: 'image' | 'file') => {
      if (!canUploadMedia) {
        console.warn('RichTextEditor: upload requested without upload context', {
          type,
          uploadContextId,
        });
        return;
      }

      setMediaError(null);

      if (type === 'image') {
        const objectUrl = URL.createObjectURL(file);
        pendingUploadsRef.current.set(objectUrl, { file, type, objectUrl });
        insertImage(objectUrl, file.name);
      } else {
        const marker = `${PENDING_FILE_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2)}`;
        pendingUploadsRef.current.set(marker, { file, type });
        insertFileLink(marker, file.name);
      }

      setPendingUploadsCount(pendingUploadsRef.current.size);
      scheduleResolvedMediaSync();
    },
    [canUploadMedia, insertFileLink, insertImage, scheduleResolvedMediaSync, uploadContextId]
  );

  const flushPendingUploads = React.useCallback(async () => {
    const entries = Array.from(pendingUploadsRef.current.entries());
    if (!entries.length) {
      return;
    }

    setMediaError(null);
    setIsFlushingPending(true);

    try {
      let updatedHtml = editor?.getHTML() ?? value;

      for (const [marker, pending] of entries) {
        const uploadedUrl = await uploadMedia(pending.file, pending.type);
        updatedHtml = updatedHtml.split(marker).join(uploadedUrl);

        if (pending.objectUrl) {
          URL.revokeObjectURL(pending.objectUrl);
        }

        pendingUploadsRef.current.delete(marker);
      }

      setPendingUploadsCount(pendingUploadsRef.current.size);

      if (editor) {
        editor.commands.setContent(updatedHtml, { emitUpdate: false });
      }

      onChange(normalizeContentForPersisting(updatedHtml));
      scheduleResolvedMediaSync();
    } catch (error) {
      setMediaError('Failed to upload attached media. Please try again.');
      throw error;
    } finally {
      setIsFlushingPending(false);
    }
  }, [
    editor,
    normalizeContentForPersisting,
    onChange,
    scheduleResolvedMediaSync,
    uploadMedia,
    value,
  ]);

  React.useEffect(() => {
    const pendingUploads = pendingUploadsRef.current;

    return () => {
      pendingUploads.forEach((pending) => {
        if (pending.objectUrl) {
          URL.revokeObjectURL(pending.objectUrl);
        }
      });
      pendingUploads.clear();
      setPendingUploadsCount(0);
    };
  }, []);

  const handleImageInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      void handleUpload(file, 'image');
    },
    [handleUpload]
  );

  const handleFileInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      void handleUpload(file, 'file');
    },
    [handleUpload]
  );

  return {
    imageInputRef,
    fileInputRef,
    isFlushingPending,
    mediaError,
    hasPendingUploads: pendingUploadsCount > 0,
    flushPendingUploads,
    handleImageInputChange,
    handleFileInputChange,
    handleUpload,
  };
};
